import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ChevronLeft, Clock } from "lucide-react";
import StoreShell from "@/components/landing/StoreShell";
import { LangToggle } from "@/pages/BlogIndexPage";
import { BLOG_I18N, urduProps } from "@/content/blogI18n";
import { useSeo, faqJsonLd } from "@/hooks/useSeo";

export default function BlogPostPage({ lang = "en" }) {
    const { slug } = useParams();
    const t = BLOG_I18N[lang];
    const post = t.posts.find((p) => p.slug === slug);
    const { className: langClass, ...langAttrs } = urduProps(lang);
    const isUr = lang === "ur";

    useSeo({
        title: post ? `${post.title} | ${isUr ? "ریشم ڈراپس" : "Resham Drops"}` : "",
        description: post?.description,
        keywords: post?.keywords,
        path: `${t.base}/${slug}`,
        jsonLd: post
            ? {
                  "@context": "https://schema.org",
                  "@graph": [
                      {
                          "@type": "BlogPosting",
                          headline: post.title,
                          description: post.description,
                          datePublished: post.date,
                          dateModified: post.date,
                          author: { "@type": "Organization", name: "Resham Drops" },
                          publisher: { "@type": "Organization", name: "Resham Drops" },
                          mainEntityOfPage: `${window.location.origin}${t.base}/${post.slug}`,
                          inLanguage: t.locale,
                      },
                      faqJsonLd(post.faqs),
                  ],
              }
            : null,
    });

    if (!post) return <Navigate to={t.base} replace />;
    const related = t.posts.filter((p) => p.slug !== post.slug);

    return (
        <StoreShell testid={isUr ? "ur-blog-post-page" : "blog-post-page"}>
            <main {...langAttrs} className={`${langClass} max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20`}>
                <div className="flex items-center justify-between gap-4">
                    <Link to={t.base} data-testid="blog-back-link" className="inline-flex items-center gap-1 text-sm font-semibold text-forest-700 hover:text-gold-600">
                        <ChevronLeft className={`w-4 h-4 ${isUr ? "rotate-180" : ""}`} /> {t.back}
                    </Link>
                    <LangToggle to={`${t.other}/${post.slug}`} label={t.otherLabel} className={t.otherClass} testid="blog-post-lang-toggle" />
                </div>
                <h1 className="font-serif font-bold text-forest-800 text-4xl sm:text-5xl leading-tight mt-5">{post.title}</h1>
                <div className="flex items-center gap-2 text-xs text-forest-700/70 font-semibold mt-4">
                    <Clock className="w-3.5 h-3.5" /> {t.mins(post.readMins)} · {isUr ? "ریشم ڈراپس" : "Resham Drops"}
                </div>
                <article className="mt-10 space-y-8">
                    {post.sections.map((s) => (
                        <section key={s.h}>
                            <h2 className="font-serif font-bold text-forest-800 text-base md:text-lg">{s.h}</h2>
                            <p className="text-sm sm:text-base text-forest-900/80 mt-2 leading-relaxed">{s.p}</p>
                        </section>
                    ))}
                </article>
                <section className="mt-12" aria-label="FAQ">
                    <h2 className="font-serif font-bold text-forest-800 text-base md:text-lg">{t.faq}</h2>
                    <div className="mt-4 space-y-3">
                        {post.faqs.map((f) => (
                            <div key={f.q} className="bg-white rounded-xl border border-forest-100 p-5">
                                <p className="font-bold text-forest-900 text-sm sm:text-base">{f.q}</p>
                                <p className="text-sm text-forest-900/75 mt-2">{f.a}</p>
                            </div>
                        ))}
                    </div>
                </section>
                <div className="mt-12 bg-forest-900 rounded-2xl p-6 sm:p-8 border border-gold-500/30">
                    <p className="font-serif font-bold text-gold-400 text-lg">{t.ctaTitle}</p>
                    <p className="text-cream/75 text-sm mt-1">{t.ctaSub}</p>
                    <Link
                        to="/product/dermalist-rosemary-hair-growth-serum"
                        data-testid="blog-cta-serum"
                        className="mt-4 inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-forest-900 font-bold text-sm px-5 py-3 rounded-full transition-colors"
                    >
                        {t.ctaBtn} <ArrowRight className={`w-4 h-4 ${isUr ? "rotate-180" : ""}`} />
                    </Link>
                </div>
                <nav className="mt-12" aria-label="Related articles">
                    <h2 className="font-serif font-bold text-forest-800 text-base md:text-lg">{t.more}</h2>
                    <ul className="mt-3 space-y-2">
                        {related.map((p) => (
                            <li key={p.slug}>
                                <Link to={`${t.base}/${p.slug}`} className="text-sm font-semibold text-forest-700 hover:text-gold-600 underline-offset-4 hover:underline">
                                    {p.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>
            </main>
        </StoreShell>
    );
}
